import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  approveOrDenyParcel,
  cancelPreApproval,
} from "../../../services/resident_api";
import approvedBadge from "../../../assets/images/verify.png";
import cap from "../../../assets/images/cap.png";
import companyIcon from "../../../assets/images/parcel1.png";
import helpIcon from "../../../assets/images/help.png";
import addIcon from "../../../assets/images/add.png";
import AddParcelModal from "../../ResidentParcelDashboardSection/ServicesSection/AddParcelModal";
import ShareInvite from "../ServicesSection/ShareInvite";
import ScreenshotModal from "../ServicesSection/ScreenshotModal";
import ApproveNowModal from "../ServicesSection/ApproveNowModal";
import UnlockNowModal from "../ServicesSection/UnlockNowModal";
import CancelPreApprovedModal from "./CancelPreApprovedModal";
import CaptureImageModal from "./CaptureImageModal";
import AddMemberModal from "../../ResidentParcelDashboardSection/ServicesSection/AddMemberModal";
import HelpModal from "./HelpModal";
import NoData from "../ServicesSection/NoData";
import boy_relax from "../../../assets/images/boy_relax.png";
import girl_relax from "../../../assets/images/girl_relax.png";
import add_parcel from "../../../assets/images/add_parcel.png";
import trashIcon from "../../../assets/images/trash.png";
import { v4 as uuidv4 } from "uuid";
import { format, parseISO } from "date-fns";
import { fetchMemberListAPI } from "../../../services/resident_api";

const ServicesSection = ({
  activeTab,
  residentSessionData,
  resident_id,
  preApprovedList,
  role,
  fetchResidentData,
  residentName,
  residentPhone,
}) => {
  // === State Management ===
  const [isPending, setIsPending] = useState(true);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(null);
  const [modalScreenshots, setModalScreenshots] = useState([]);
  const [isUnlockOpen, setUnlockOpen] = useState(false);
  const [residentId, setResidentId] = useState(null);
  const [qrCodeImg, setQrcodeImg] = useState(null);
  const [LockerOpenOtp, setLockerOpenOtp] = useState(null);
  const [isApproveModalOpen, setApproveModalOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [manualUpcoming, setManualUpcoming] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState({
    approval_id: null,
    logoSrc: "",
  });
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [selectedAvatarIndex, setSelectedAvatarIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isHelpModalOpen, setHelpModalOpen] = useState(false);

  // === Memoized Data Processing ===
  const {
    pending = [],
    approved = [],
    stored = [],
    rejected = [],
    totals = {},
  } = residentSessionData || {};

  // Memoized session lists with stable dependencies
  const memoizedPending = useMemo(
    () => pending,
    [pending?.length, JSON.stringify(pending)]
  );
  const memoizedApproved = useMemo(
    () => approved,
    [approved?.length, JSON.stringify(approved)]
  );
  const memoizedStored = useMemo(
    () => stored,
    [stored?.length, JSON.stringify(stored)]
  );
  const memoizedRejected = useMemo(
    () => rejected,
    [rejected?.length, JSON.stringify(rejected)]
  );
  const stablePreApprovedList = useMemo(
    () => (Array.isArray(preApprovedList) ? preApprovedList : []),
    [preApprovedList?.length, JSON.stringify(preApprovedList)]
  );

  // Memoized processed data for current tab
  const processedTabData = useMemo(() => {
    if (activeTab === "Upcoming") {
      return {
        manualUpcoming: stablePreApprovedList,
        filteredSessions: [],
      };
    }

    if (!["Approval", "Stored", "Rejected"].includes(activeTab)) {
      return { manualUpcoming: [], filteredSessions: [] };
    }

    let sessions = [];
    if (activeTab === "Approval") {
      sessions = isPending ? memoizedPending : memoizedApproved;
    } else if (activeTab === "Stored") {
      sessions = memoizedStored;
    } else if (activeTab === "Rejected") {
      sessions = memoizedRejected;
    }

    return {
      manualUpcoming: [],
      filteredSessions: sessions,
    };
  }, [
    activeTab,
    isPending,
    memoizedPending,
    memoizedApproved,
    memoizedStored,
    memoizedRejected,
    stablePreApprovedList,
  ]);

  // === Single useEffect for state updates ===
  useEffect(() => {
    setManualUpcoming(processedTabData.manualUpcoming);
    setFilteredSessions(processedTabData.filteredSessions);
  }, [processedTabData]);

  // === Mobile detection ===
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // === Fetch members when role is primary ===
  useEffect(() => {
    if (role === "primary") {
      fetchMembers();
    }
  }, [role]);

  // === Memoized functions ===
  const fetchMembers = useCallback(async () => {
    try {
      const res = await fetchMemberListAPI();
      if (res.success) {
        setMemberList(res.data.members || []);
        setTotalMembers(res.data.total_members || 0);
      }
    } catch (err) {
      console.error("Failed to fetch member list:", err);
    }
  }, []);

  const handleHelpClick = useCallback(() => {
    if (isMobile) {
      window.location.href = "tel:9892233193";
    } else {
      setHelpModalOpen(true);
    }
  }, [isMobile]);

  const openCancelModal = useCallback((approvalId, logoUrl) => {
    setCancelTarget({ approval_id: approvalId, logoSrc: logoUrl });
    setShowCancelModal(true);
  }, []);

  const handleAddParcel = useCallback(
    async (data) => {
      setAddModalOpen(false);
      await fetchResidentData();
    },
    [fetchResidentData]
  );

  const handleApproveOrDeny = useCallback(
    async (isApprove, otpValue = "") => {
      if (!approveTarget) return;

      const payload = {
        company_nm: approveTarget.company,
        resident_name: approveTarget.resident_name,
        number_of_parcels: approveTarget.parcels,
        resident_token: approveTarget.resident_token,
        text_input_da_id: approveTarget.text_input_da_id,
        text_input_da_name: approveTarget.deliveryBoy,
        complex_name: approveTarget.complex_name,
        complex_location: approveTarget.complex_location,
        text_input_file_ident_code: approveTarget.text_input_file_ident_code,
        action: isApprove,
        // Use ternary: if OTP is not required OR no OTP value provided, send "None"
        text_input_resident_otp_value:
          approveTarget.text_input_resident_req_otp !== "required" || !otpValue
            ? "None"
            : otpValue,
        text_input_resident_req_otp: approveTarget.text_input_resident_req_otp, // With 'req'
      };

      // DEBUG: Log the actual payload
      console.log("=== DEBUG: Payload being sent ===");
      console.log("Payload keys:", Object.keys(payload));
      console.log("Full payload:", JSON.stringify(payload, null, 2));
      console.log("=== END DEBUG ===");

      try {
        const response = await approveOrDenyParcel(payload);
        await fetchResidentData();
        return response;
      } catch (err) {
        console.error("Approve/Deny error:", err);
        const errorMessage =
          err?.response?.data?.message || err?.message || "Request failed";
        throw new Error(errorMessage);
      }
    },
    [approveTarget, fetchResidentData]
  );

  // === Memoized helper functions ===
  const getRandomColor = useCallback((name) => {
    const colors = ["#BAE8F8", "#DDDCE2", "#CCEBBF", "#E8D8F7", "#FFDDE7"];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  }, []);

  //Date and time  format
  const formatTime = useCallback((timeString) => {
    if (!timeString) return "";

    // Handle time with seconds (HH:MM:SS)
    const [hours, minutes, seconds] = timeString.split(":");
    const hour = parseInt(hours);

    // Convert 24-hour to 12-hour format
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12; // Convert 0 to 12, 13 to 1, etc.

    return `${formattedHour}:${minutes} ${ampm}`;
  }, []);

  const renderNoData = useCallback(
    (img, title, linkText) => (
      <div className="col-span-full flex justify-center py-6">
        <div className="w-full max-w-5xl px-4">
          <NoData imageSrc={img} title={title} linkText={linkText} />
        </div>
      </div>
    ),
    []
  );

  // === Memoized tab handlers ===
  const handlePendingClick = useCallback(() => {
    setIsPending(true);
  }, []);

  const handleApprovedClick = useCallback(() => {
    setIsPending(false);
  }, []);

  // === Memoized card renderer ===
  const renderCard = useCallback(
    (item, type) => {
      return (
        <div
          key={`${item.id}`}
          className="bg-white w-full rounded-xl shadow-md overflow-hidden flex flex-col"
        >
          {/* === Card Header === */}
          <div className="flex items-center justify-between bg-blue-100 px-3 py-2 gap-2">
            {type === "Upcoming" ? (
              <>
                <img
                  src={item.company_img || companyIcon}
                  alt="Company"
                  className="w-9 h-9 rounded-md object-cover aspect-square"
                  onError={(e) => {
                    e.target.src = companyIcon;
                  }}
                />

                <div className="flex-1 flex justify-center">
                  <button className="flex items-center bg-[#063367] px-3 py-1.5 rounded-lg hover:opacity-90 transition">
                    <span className="text-white text-[13.5px] font-semibold mr-1.5">
                      Pre-Approved
                    </span>
                    <img
                      src={approvedBadge}
                      alt="Verified"
                      className="w-5 h-5 object-contain"
                    />
                  </button>
                </div>

                <button
                  className="ml-2 text-gray-600 hover:text-red-600"
                  onClick={() =>
                    openCancelModal(
                      item.approval_id,
                      item.company_img || companyIcon
                    )
                  }
                >
                  <img src={trashIcon} alt="Remove" className="w-8 h-8" />
                </button>
              </>
            ) : (
              <>
                <img
                  src={item.company_img || companyIcon}
                  alt="Company"
                  className="w-9 h-9 rounded-md object-cover aspect-square"
                  onError={(e) => {
                    e.target.src = companyIcon;
                  }}
                />

                <div className="flex flex-1 justify-start items-center">
                  {type === "Stored" ? (
                    <span className="text-base text-black font-semibold">
                      <span className="text-base text-black font-bold ml-1">
                        {item.company?.toUpperCase()}
                      </span>
                    </span>
                  ) : (
                    <span className="text-base text-black font-semibold ">
                      {item.company}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* === Card Body === */}
          {type === "Upcoming" ? (
            <div className="p-3">
              <div className="flex justify-between mb-1">
                <span className="text-sm text-black-600">Company Name -</span>
                <span className="text-sm font-semibold text-black">
                  {item.company_nm?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-black-600">Arrival Date -</span>
                <span className="text-sm font-semibold text-black">
                  {format(parseISO(item.pre_approved_start), "dd/MM/yyyy")} –{" "}
                  {format(parseISO(item.pre_approved_end), "dd/MM/yyyy")}
                </span>
              </div>

              {/* otp */}
              {item.text_input_resident_otp_value &&
                item.text_input_resident_otp_value !== "null" &&
                item.text_input_resident_otp_value !== "None" &&
                item.text_input_resident_otp_value !== "" && (
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-black-600">OTP -</span>
                    <span className="text-sm font-semibold text-red-600">
                      {item.text_input_resident_otp_value}
                    </span>
                  </div>
                )}

              <div className="text-sm text-black-600 mt-1">
                Delivery Details Screenshot :-
              </div>

              {type === "Upcoming" && item.screenshots?.length > 0 && (
                <div
                  className="relative mt-3 cursor-pointer"
                  onClick={() => {
                    setModalScreenshots(item.screenshots);
                    setModalIndex(0);
                  }}
                >
                  <img
                    src={item.screenshots[0]}
                    alt="Preview"
                    className="w-full h-20 object-cover rounded-lg shadow-sm"
                  />

                  {item.screenshots.length > 1 && (
                    <span className="absolute top-2 right-2 bg-blue-600 text-white text-sm rounded-full w-6 h-6 flex items-center justify-center shadow-md">
                      {item.screenshots.length}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex p-2 gap-2 items-center">
              <img
                src={
                  Array.isArray(item.parcels_screenshots)
                    ? item.parcels_screenshots[0] || cap
                    : cap
                }
                alt="Parcel"
                className="w-16 h-16 rounded-md object-cover aspect-square cursor-pointer"
                onClick={() => {
                  setModalImages(
                    Array.isArray(item.parcels_screenshots)
                      ? item.parcels_screenshots
                      : [cap]
                  );
                  setImageModalOpen(true);
                }}
              />

              <div className="flex flex-col justify-between flex-1">
                <div>
                  <div className="text-sm text-black-600">
                    Delivery Agent -
                    <span className="font-semibold text-black ml-1">
                      {item.deliveryBoy}
                    </span>
                  </div>
                  <div className="text-sm text-black-600 mt-1">
                    Received On -
                    <span className="font-semibold text-black ml-1">
                      {formatTime(item.time)} {item.date}
                    </span>
                  </div>
                  {/* ADDED OTP DISPLAY FOR APPROVED ITEMS */}
                  {type === "Approval" &&
                    item.status === "True" &&
                    item.resident_otp_value && (
                      <div className="text-sm text-black-600 mt-1">
                        OTP -
                        <span className="font-semibold text-red-600 ml-1">
                          {item.resident_otp_value}
                        </span>
                      </div>
                    )}
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-blue-500 font-semibold">
                    {item.parcels} Parcel{item.parcels > 1 ? "s" : ""}
                  </span>

                  {type === "Approval" && item.status === "pending" && (
                    <div className="flex items-center gap-2">
                      {item.deny_count > 0 && (
                        <span className="text-red-500 text-xs font-semibold">
                          {item.deny_count} time{item.deny_count > 1 ? "s" : ""}{" "}
                          deny
                        </span>
                      )}

                      <button
                        className="bg-blue-500 text-white px-3 py-2 rounded-md text-sm font-medium shadow hover:bg-blue-600 transition"
                        onClick={() => {
                          setApproveTarget(item);
                          setApproveModalOpen(true);
                        }}
                      >
                        Approve Now
                      </button>
                    </div>
                  )}

                  {type === "Approval" && item.status === "True" && (
                    <img
                      src={approvedBadge}
                      alt="Verified"
                      className="w-10 h-10"
                    />
                  )}

                  {type === "Stored" && (
                    <button
                      onClick={() => {
                        setQrcodeImg(item.qrcode_public_url);
                        setLockerOpenOtp(item.locker_open_otp);
                        setUnlockOpen(true);
                      }}
                      className="bg-blue-500 text-white px-3 py-2 rounded-md text-sm font-medium shadow hover:bg-blue-600 transition"
                    >
                      Unlock Now
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {modalIndex !== null && modalScreenshots.length > 0 && (
            <ScreenshotModal
              screenshots={modalScreenshots}
              startIndex={modalIndex}
              onClose={() => {
                setModalIndex(null);
                setModalScreenshots([]);
              }}
            />
          )}
        </div>
      );
    },
    [openCancelModal, formatTime]
  );

  // === Memoized main content ===
  const tabContent = useMemo(() => {
    if (activeTab === "Members") {
      return (
        <div className="w-full max-w-5xl mx-auto px-4 mt-6">
          <ShareInvite />
        </div>
      );
    }

    // Handle No Data Screens
    if (activeTab === "Approval" && isPending && totals.pending === 0) {
      return renderNoData(
        boy_relax,
        "Be Relax : ) You",
        "Don't Have Any Pending Approvals"
      );
    }

    if (activeTab === "Approval" && !isPending && totals.approved === 0) {
      return renderNoData(
        boy_relax,
        "Be Relax : ) You",
        "Don't Have Any Approved Parcels"
      );
    }

    if (activeTab === "Stored" && totals.stored === 0) {
      return renderNoData(
        girl_relax,
        "Be Relax : ) You",
        "Don't Have Any Parcels In The Lockers"
      );
    }

    if (activeTab === "Upcoming" && manualUpcoming.length === 0) {
      return renderNoData(
        add_parcel,
        "You Don't Have Any Upcoming Parcels",
        "Shop And + Add Parcels Now!"
      );
    }

    // Render Upcoming tab data
    if (activeTab === "Upcoming" && Array.isArray(manualUpcoming)) {
      return manualUpcoming.map((session, index) => {
        const cardProps = {
          ...session,
          id: `${session.company_name}-${index}`,
          approval_id: session.id,
          company_nm: session.company_name,
          company_img: session.company_img_url,
          pre_approved_start: session.start_date,
          pre_approved_end: session.end_date,
          status: "Approved",
          screenshots: session.screenshot_urls,
          // Use text_input_resident_otp_value instead of otp
          text_input_resident_otp_value: session.resident_otp_value || null,
          residentName,
          residentPhone,
        };

        return renderCard(cardProps, activeTab);
      });
    }

    // Render other tabs data
    return filteredSessions.flatMap((session) => {
      return session.deliveries.map((delivery, deliveryIndex) => {
        const cardProps = {
          ...delivery,
          id: `${session.da_id}-${uuidv4()}`,
          deliveryBoy: session.da_name,
          company: session.company_nm,
          company_img: session.company_img,
          resident_id: delivery.resident_id,
          resident_token: delivery.resident_token,
          parcels: delivery.number_of_parcel,
          time: delivery.approval_sent_time?.split(" ")[1] || "",
          date: delivery.approval_sent_time?.split(" ")[0] || "",
          status: delivery.is_authenticated,
          // CHANGED: Use public_url instead of shortlink
          parcels_screenshots: Array.isArray(delivery.captured_images?.links)
            ? delivery.captured_images.links.map(
                (linkObj) => linkObj.public_url // Changed from shortlink to public_url
              )
            : [],
          qrcode_shortlink: delivery.resident_qrcode_shortlink,
          qrcode_public_url: delivery.resident_qrcode_public_url,
          locker_open_otp: delivery.resident_locker_open_otp,
          text_input_da_id: delivery.text_input_da_id ?? session.da_id,
          text_input_file_ident_code:
            delivery.text_input_file_ident_code ?? session.file_ident_code,

          text_input_resident_req_otp:
            delivery.resident_req_otp || "not_required",
          resident_otp_value: delivery.resident_otp_value,
        };

        return renderCard(cardProps, activeTab);
      });
    });
  }, [
    activeTab,
    isPending,
    totals,
    manualUpcoming,
    filteredSessions,
    residentName,
    residentPhone,
    renderNoData,
    renderCard,
  ]);

  return (
    <section className="max-w-full ">
      <div className="bg-[#f9f9f9] max-w-[1200px] mx-auto rounded-2xl p-4">
        {/* HEADER SECTION */}
        <div className="flex justify-between items-center mb-4 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            {activeTab === "Approval" && (
              <div className="w-full flex justify-center md:justify-start px-4 md:px-0 ">
                <div className="inline-flex bg-white rounded-full shadow overflow-hidden">
                  <button
                    onClick={handlePendingClick}
                    className={`flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold tracking-wide focus:outline-none ${
                      isPending
                        ? "bg-red-500 text-white"
                        : "text-black hover:bg-gray-100"
                    } rounded-l-full`}
                  >
                    <span>PENDING</span>
                    <span
                      className={`ml-2 text-xs sm:text-sm font-bold w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full ${
                        isPending
                          ? "bg-red-900 text-white"
                          : "bg-gray-300 text-black"
                      }`}
                    >
                      {totals.pending}
                    </span>
                  </button>

                  <button
                    onClick={handleApprovedClick}
                    className={`flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold tracking-wide focus:outline-none ${
                      !isPending
                        ? "bg-blue-500 text-white"
                        : "text-black hover:bg-gray-100"
                    } rounded-r-full`}
                  >
                    <span>APPROVED</span>
                    <span
                      className={`ml-2 text-xs sm:text-sm font-bold w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full ${
                        !isPending
                          ? "bg-blue-900 text-white"
                          : "bg-gray-300 text-black"
                      }`}
                    >
                      {totals.approved}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === "Stored" && (
              <div>
                <h2 className="text-lg font-semibold text-black">
                  Stored Packages
                </h2>
                <p className="text-sm text-gray-600">
                  <span className="text-blue-500 font-semibold">
                    {totals.stored} Parcels
                  </span>{" "}
                  Waiting For You
                </p>
              </div>
            )}

            {activeTab === "Upcoming" && (
              <div>
                <h2 className="text-lg font-semibold text-black">
                  Upcoming Packages
                </h2>
                <p className="text-sm text-gray-600">
                  <span className="text-blue-500 font-semibold">
                    {Array.isArray(preApprovedList)
                      ? preApprovedList.length
                      : 0}{" "}
                    Parcels
                  </span>{" "}
                  are Pre-Approved
                </p>
              </div>
            )}

            {activeTab === "Members" && role === "primary" && (
              <>
                <div className="w-full px-4 sm:px-6 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {memberList.map((member, index) => (
                      <div key={index} className="relative group">
                        <div
                          title={member.name}
                          onClick={() =>
                            window.innerWidth < 1024 &&
                            setSelectedAvatarIndex(
                              selectedAvatarIndex === index ? null : index
                            )
                          }
                          className="w-12 h-12 rounded-full flex items-center justify-center text-black font-bold text-lg cursor-pointer"
                          style={{
                            backgroundColor: getRandomColor(member.name),
                          }}
                        >
                          {member.name?.charAt(0).toUpperCase()}
                        </div>

                        {selectedAvatarIndex === index && (
                          <div className="absolute top-14 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded shadow-md z-10 block lg:hidden">
                            {member.name}
                          </div>
                        )}

                        <div className="hidden lg:group-hover:block absolute top-14 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded shadow-md z-10">
                          {member.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <AddMemberModal
                  isOpen={showModal}
                  onClose={() => setShowModal(false)}
                  onMemberAdded={fetchMembers}
                />
              </>
            )}
          </div>

          <div className="flex items-center gap-2 mt-3 sm:mt-0">
            {activeTab === "Stored" && (
              <div className="flex items-center gap-1">
                <button onClick={handleHelpClick} className="p-2 rounded-full">
                  <img
                    src={helpIcon}
                    alt="Help"
                    className="w-9 h-9 object-contain"
                  />
                </button>
                <button
                  onClick={handleHelpClick}
                  className="text-sm text-black font-medium"
                >
                  Help
                </button>

                <HelpModal
                  isOpen={isHelpModalOpen}
                  onClose={() => setHelpModalOpen(false)}
                />
              </div>
            )}

            {activeTab === "Upcoming" && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setAddModalOpen(true);
                    setResidentId(resident_id);
                  }}
                  className="ml-1 rounded-full  shadow hover:bg-gray-100 transition"
                >
                  <img
                    src={addIcon}
                    alt="Add Parcel"
                    className="w-9 h-9 object-contain"
                  />
                </button>
                <span className="text-sm text-black font-medium">
                  Add Parcel
                </span>
              </div>
            )}

            {activeTab === "Members" && role === "primary" && (
              <div className="w-full px-4 sm:px-6 py-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowModal(true)}
                    className="rounded-full shadow hover:bg-gray-100 transition"
                  >
                    <img
                      src={addIcon}
                      alt="Add Member"
                      className="w-10 h-10 object-contain"
                    />
                  </button>

                  <div className="flex flex-col">
                    <span className="text-base font-semibold text-black">
                      Add Members
                    </span>
                    <span className="text-sm text-blue-500 font-medium">
                      {totalMembers} Member{totalMembers !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RENDER MEMOIZED CONTENT */}
        {activeTab === "Members" ? (
          tabContent
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tabContent}
          </div>
        )}

        {/* Modals */}
        <AddParcelModal
          isOpen={isAddModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSubmit={handleAddParcel}
          residentId={residentId}
          residentName={residentName}
          residentPhone={residentPhone}
        />

        <ApproveNowModal
          isOpen={isApproveModalOpen}
          onClose={() => {
            setApproveModalOpen(false);
            setApproveTarget(null);
          }}
          logoSrc={approveTarget?.company_img || companyIcon}
          onApprove={(otp) => handleApproveOrDeny("True", otp)} // Pass OTP here
          onDeny={() => handleApproveOrDeny("False")}
          otpRequired={
            approveTarget?.text_input_resident_req_otp === "required"
          } // This stays the same
        />

        <UnlockNowModal
          isOpen={isUnlockOpen}
          residentId={residentId}
          qrCode_img_src={qrCodeImg}
          otp_req={LockerOpenOtp}
          onClose={() => setUnlockOpen(false)}
        />

        <CancelPreApprovedModal
          isOpen={showCancelModal}
          logoSrc={cancelTarget.logoSrc}
          onClose={() => {
            setShowCancelModal(false);
            setCancelTarget({ approval_id: null, logoSrc: "" });
          }}
          onConfirm={async () => {
            if (!cancelTarget.approval_id) return;
            await cancelPreApproval(cancelTarget.approval_id);
            setManualUpcoming((prev) =>
              prev.filter((s) => s.id !== cancelTarget.approval_id)
            );
            setShowCancelModal(false);
            setCancelTarget({ approval_id: null, logoSrc: "" });
          }}
        />

        <CaptureImageModal
          isOpen={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          images={modalImages}
        />

        {modalIndex !== null && modalScreenshots.length > 0 && (
          <ScreenshotModal
            screenshots={modalScreenshots}
            startIndex={modalIndex}
            onClose={() => {
              setModalIndex(null);
              setModalScreenshots([]);
            }}
          />
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
